import { AdminPermissionClaims } from "@freetech/models/user";

/**
 * Create an empty admin permissions object with all permissions set to false
 */
export function createEmptyAdminPermissions(): AdminPermissionClaims {
  return {
    home: {
      view: "freelancer_admin",
    },
    timetracker: {
      freelancer_or_admin_view: "freelancer",
    },
    freelo: {
      view: "view_my_boards",
      create: false,
      modify: false,
      delete: false,
    },
    financialHub: {
      view: false,
    },
    clients: {
      view: false,
      create: false,
      modify: false,
      delete: false,
      invoicing: false,
      clientIdRestrictions: [],
    },
    freelancers: {
      view: false,
      create: false,
      modify: false,
      delete: false,
      payroll: false,
      freelancerIdRestrictions: [],
    },
    projectAssignments: {
      view: false,
      create: false,
      modify: false,
      delete: false,
      sign: false,
    },
    timesheets: {
      view: false,
      create: false,
      modify: false,
      delete: false,
      approve_or_reject: false,
    },
    blogs: {
      view: false,
      create: false,
      modify: false,
      delete: false,
      make_public: false,
    },
    jobs: {
      view: false,
      create: false,
      modify: false,
      delete: false,
      make_public: false,
    },
    reviews: {
      view: false,
      create: false,
      modify: false,
      delete: false,
    },
    email_management: {
      view: false,
      create: false,
      modify: false,
      delete: false,
    },
    sms_management: {
      view: false,
      create: false,
      modify: false,
      delete: false,
    },
    user_management: {
      view: false,
      create: false,
      modify: false,
      delete: false,
    },
    audit_log: {
      view: false,
    },
    system: {
      view: false,
      modify: false,
    },
    scheduling: {
      view: false,
    },
  };
}

/**
 * Owner permissions - full access to everything (previously Super Admin)
 */
export const OWNER_PERMISSIONS: AdminPermissionClaims = {
  home: {
    view: "owner",
  },
  timetracker: {
    freelancer_or_admin_view: "admin",
  },
  freelo: {
    view: "view_all",
    create: true,
    modify: true,
    delete: true,
  },
  financialHub: {
    view: true,
  },
  clients: {
    view: true,
    create: "all",
    modify: "all",
    delete: "all",
    invoicing: true,
  },
  freelancers: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    payroll: true,
  },
  projectAssignments: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    sign: true,
  },
  timesheets: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    approve_or_reject: true,
  },
  blogs: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    make_public: true,
  },
  jobs: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    make_public: true,
  },
  reviews: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  email_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  sms_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  user_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  audit_log: {
    view: true,
  },
  system: {
    view: true,
    modify: true,
  },
  scheduling: {
    view: true,
  },
};

/**
 * Sales Admin permissions - focused on sales and client management
 */
export const SALES_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  home: {
    view: "sales_admin",
  },
  timetracker: {
    freelancer_or_admin_view: "admin",
  },
  freelo: {
    view: "view_all",
    create: true,
    modify: true,
    delete: false,
  },
  financialHub: {
    view: false,
  },
  clients: {
    view: true,
    create: "all",
    modify: "all",
    delete: "all",
    invoicing: true,
  },
  freelancers: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    payroll: false,
  },
  projectAssignments: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    sign: false,
  },
  timesheets: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    approve_or_reject: true,
  },
  blogs: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    make_public: false,
  },
  jobs: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    make_public: false,
  },
  reviews: {
    view: true,
    create: true,
    modify: true,
    delete: false,
  },
  email_management: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  sms_management: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  user_management: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  audit_log: {
    view: false,
  },
  system: {
    view: false,
    modify: false,
  },
  scheduling: {
    view: false,
  },
};

/**
 * Human Resources Admin permissions - focused on freelancer and user management
 */
export const HUMAN_RESOURCES_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  home: {
    view: "human_resources_admin",
  },
  timetracker: {
    freelancer_or_admin_view: "admin",
  },
  freelo: {
    view: "view_all",
    create: false,
    modify: false,
    delete: false,
  },
  financialHub: {
    view: false,
  },
  clients: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    invoicing: true,
  },
  freelancers: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    payroll: true,
  },
  projectAssignments: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    sign: true,
  },
  timesheets: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    approve_or_reject: true,
  },
  blogs: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    make_public: false,
  },
  jobs: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    make_public: true,
  },
  reviews: {
    view: true,
    create: false,
    modify: false,
    delete: false,
  },
  email_management: {
    view: true,
    create: true,
    modify: true,
    delete: false,
  },
  sms_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  user_management: {
    view: true,
    create: true,
    modify: true,
    delete: false,
  },
  audit_log: {
    view: true,
  },
  system: {
    view: false,
    modify: false,
  },
  scheduling: {
    view: false,
  },
};

/**
 * Executive Assistant Admin permissions - administrative support with broad access
 */
export const EXECUTIVE_ASSISTANT_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  home: {
    view: "executive_assistant_admin",
  },
  timetracker: {
    freelancer_or_admin_view: "admin",
  },
  freelo: {
    view: "view_all",
    create: false,
    modify: false,
    delete: false,
  },
  financialHub: {
    view: false,
  },
  clients: {
    view: true,
    create: "all",
    modify: "all",
    delete: "all",
    invoicing: true,
  },
  freelancers: {
    view: true,
    create: false,
    modify: true,
    delete: false,
    payroll: true,
  },
  projectAssignments: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    sign: false,
  },
  timesheets: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    approve_or_reject: false,
  },
  blogs: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    make_public: false,
  },
  jobs: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    make_public: false,
  },
  reviews: {
    view: true,
    create: false,
    modify: false,
    delete: false,
  },
  email_management: {
    view: true,
    create: true,
    modify: true,
    delete: false,
  },
  sms_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  user_management: {
    view: true,
    create: false,
    modify: false,
    delete: false,
  },
  audit_log: {
    view: false,
  },
  system: {
    view: false,
    modify: false,
  },
  scheduling: {
    view: false,
  },
};

/**
 * Marketing Admin permissions - focused on content and public-facing materials
 */
export const MARKETING_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  home: {
    view: "marketing_admin",
  },
  timetracker: {
    freelancer_or_admin_view: "freelancer",
  },
  freelo: {
    view: "view_all",
    create: false,
    modify: false,
    delete: false,
  },
  financialHub: {
    view: false,
  },
  clients: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    invoicing: false,
  },
  freelancers: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    payroll: false,
  },
  projectAssignments: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    sign: false,
  },
  timesheets: {
    view: false,
    create: false,
    modify: false,
    delete: false,
    approve_or_reject: false,
  },
  blogs: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    make_public: true,
  },
  jobs: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    make_public: true,
  },
  reviews: {
    view: true,
    create: false,
    modify: false,
    delete: false,
  },
  email_management: {
    view: true,
    create: true,
    modify: true,
    delete: false,
  },
  sms_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  user_management: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  audit_log: {
    view: false,
  },
  system: {
    view: false,
    modify: false,
  },
  scheduling: {
    view: false,
  },
};

/**
 * Freelancer Admin permissions - elevated freelancer with some admin capabilities
 */
export const FREELANCER_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  home: {
    view: "freelancer_admin",
  },
  timetracker: {
    freelancer_or_admin_view: "admin",
  },
  freelo: {
    view: "view_all",
    create: true,
    modify: true,
    delete: false,
  },
  financialHub: {
    view: false,
  },
  clients: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    invoicing: false,
  },
  freelancers: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    payroll: false,
  },
  projectAssignments: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    sign: true,
  },
  timesheets: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    approve_or_reject: false,
  },
  blogs: {
    view: true,
    create: true,
    modify: true,
    delete: false,
    make_public: false,
  },
  jobs: {
    view: true,
    create: false,
    modify: false,
    delete: false,
    make_public: false,
  },
  reviews: {
    view: true,
    create: true,
    modify: true,
    delete: false,
  },
  email_management: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  sms_management: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  user_management: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  audit_log: {
    view: false,
  },
  system: {
    view: false,
    modify: false,
  },
  scheduling: {
    view: false,
  },
};

/**
 * Permission template map for easy access
 */
export const PERMISSION_TEMPLATES = {
  owner: OWNER_PERMISSIONS,
  sales_admin: SALES_ADMIN_PERMISSIONS,
  human_resources_admin: HUMAN_RESOURCES_ADMIN_PERMISSIONS,
  executive_assistant_admin: EXECUTIVE_ASSISTANT_ADMIN_PERMISSIONS,
  marketing_admin: MARKETING_ADMIN_PERMISSIONS,
  freelancer_admin: FREELANCER_ADMIN_PERMISSIONS,
} as const;

/**
 * Get the default permission template for a role
 */
export function getDefaultPermissionsForRole(
  role: string
): AdminPermissionClaims {
  const normalizedRole = role.toLowerCase().replace(/[-_]/g, "_");

  switch (normalizedRole) {
    case "admin":
      return SALES_ADMIN_PERMISSIONS;
    case "stakeholder":
    case "babystakeholder":
      return SALES_ADMIN_PERMISSIONS;
    case "executiveassistant":
    case "executive_assistant":
      return EXECUTIVE_ASSISTANT_ADMIN_PERMISSIONS;
    case "freelancer":
      return FREELANCER_ADMIN_PERMISSIONS;
    case "student":
      return FREELANCER_ADMIN_PERMISSIONS; // Students get same permissions as freelancers
    default:
      return createEmptyAdminPermissions();
  }
}

/**
 * Get all admin permission groups/categories
 */
export function getAllAdminPermissionGroups(): string[] {
  return Object.keys(createEmptyAdminPermissions());
}

/**
 * Get all permissions for a specific group/category
 */
export function getPermissionsForGroup(
  group: keyof AdminPermissionClaims
): string[] {
  const empty = createEmptyAdminPermissions();
  const groupPermissions = empty[group];

  if (typeof groupPermissions === "object" && groupPermissions !== null) {
    return Object.keys(groupPermissions);
  }

  return [];
}

/**
 * Check if a permission value is boolean or has a different type
 */
export function getPermissionType(
  group: keyof AdminPermissionClaims,
  permission: string
): "boolean" | "select" | "array" {
  const empty = createEmptyAdminPermissions();
  const value = (empty[group] as any)?.[permission];

  if (typeof value === "boolean") {
    return "boolean";
  } else if (Array.isArray(value)) {
    return "array";
  } else {
    return "select";
  }
}

/**
 * Get available options for select-type permissions
 */
export function getPermissionOptions(
  group: keyof AdminPermissionClaims,
  permission: string
): string[] {
  if (group === "home" && permission === "view") {
    return [
      "owner",
      "sales_admin",
      "human_resources_admin",
      "executive_assistant_admin",
      "marketing_admin",
      "freelancer_admin",
    ];
  }
  if (group === "timetracker" && permission === "freelancer_or_admin_view") {
    return ["freelancer", "admin"];
  }
  if (group === "freelo" && permission === "view") {
    return ["view_all", "view_my_boards"];
  }
  if (
    group === "clients" &&
    (permission === "create" ||
      permission === "modify" ||
      permission === "delete")
  ) {
    return ["all", "freelancer_only", "stakeholder_only"];
  }
  return [];
}

/**
 * Helper function to create a permission template with specific overrides
 */
export function createPermissionTemplate(
  baseTemplate: AdminPermissionClaims = createEmptyAdminPermissions(),
  overrides: Partial<AdminPermissionClaims> = {}
): AdminPermissionClaims {
  const result = { ...baseTemplate };


  Object.keys(overrides).forEach((key) => {
    const groupKey = key as keyof AdminPermissionClaims;
    if (overrides[groupKey]) {
      result[groupKey] = {
        ...result[groupKey],
        ...overrides[groupKey],
      } as any;
    }
  });

  return result;
}

/**
 * Validate permissions object structure
 */
export function validatePermissions(
  permissions: any
): permissions is AdminPermissionClaims {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }

  const requiredGroups = getAllAdminPermissionGroups();

  return requiredGroups.every((group: string) => {
    return group in permissions && typeof permissions[group] === "object";
  });
}


export const SUPER_ADMIN_PERMISSIONS = OWNER_PERMISSIONS;
export const DEFAULT_ADMIN_PERMISSIONS = OWNER_PERMISSIONS;
export const ADMIN_PERMISSIONS = SALES_ADMIN_PERMISSIONS;
export const STAKEHOLDER_PERMISSIONS = SALES_ADMIN_PERMISSIONS;
export const EXECUTIVE_ASSISTANT_PERMISSIONS =
  EXECUTIVE_ASSISTANT_ADMIN_PERMISSIONS;
export const INVENTORY_ADMIN_PERMISSIONS = HUMAN_RESOURCES_ADMIN_PERMISSIONS;
export const SHIPPING_REPAIRS_ADMIN_PERMISSIONS =
  EXECUTIVE_ASSISTANT_ADMIN_PERMISSIONS;
export const SHIPPING_REPAIRS_PERMISSIONS = FREELANCER_ADMIN_PERMISSIONS;
export const FREELANCER_PERMISSIONS = FREELANCER_ADMIN_PERMISSIONS;
